import { Injectable } from "@angular/core";
import { DataService } from "./data.service";
import { ApplicationCacheService } from "./application-cache.service";
import { PromptType, Utils } from "./utils";
import { catchError, map, Observable, of, tap, throwError } from "rxjs";
import { environment } from "src/environments/environment";
import { HpiFindings } from "../models/hpi-findings";
import {
  INSURANCE_OCR_PROMPT,
  InsuranceOcrResult,
} from "../models/insurance-ocr.model";
import {
  PHOTO_ID_OCR_PROMPT,
  PhotoIdOcrResult,
} from "../models/photo-id-ocr.model";
import {
  LAST_COUNTER_COMBINED_OCR_PROMPT,
  LAST_COUNTER_MEDS_ALLERGIES_OCR_PROMPT,
  LastCounterCombinedOcrResult,
  LastCounterMedsAllergiesOcrResult,
  normalizeLastCounterCombinedOcrResult,
  REFERRAL_DOC_OCR_PROMPT,
  ReferralDocOcrResult,
} from "../models/referral-doc-ocr.model";

@Injectable({
  providedIn: "root",
})
export class AiService {
  constructor(
    private readonly _dataService: DataService,
    private readonly _applicationCacheService: ApplicationCacheService
  ) {}

  getChiefComplaintQuestions(file: string, prompt: string): Observable<any> {
    const url = this.apiUrlForAI + "api/v1/ai/ocr-chief-complaint";
    const formData = new FormData();
    formData.append("file", "");
    formData.append("prompt", prompt);

    const headers = {
      "auth-req": "true",
      "X-Requested-With": "XMLHttpRequest",
    };

    return this._dataService.postwithHeaders(url, formData, headers);
  }

  generatePlainTextAIResponse(prompt: string, options?: { responseType?: string; model?: string }): Observable<string> {
    const url = this.apiUrlForAI + "api/v1/ai/process-text";
    const body: { text: string; prompt: string; responseType?: string; model?: string } = { text: "", prompt };
    if (options?.responseType != null && options.responseType !== "" && options.responseType !== "Auto") {
      body.responseType = options.responseType.toLowerCase();
    }
    if (options?.model != null) {
      body.model = options.model;
    } else if (body.responseType) {
      body.model = "gpt-4o";
    }
    return this._dataService.postPlainText(url, body);
  }

  generatePlainTextStreamAIResponse(
    prompt: string,
    encounterId: string,
    type: string,
    options?: { model?: string; userId?: string; responseType?: string }
  ): Observable<string> {
    const url = this.apiUrlForAI + "api/v1/ai/process-text-stream";
    const body: { text: string; prompt: string; userId: string; type: string; model?: string; responseType?: string } = {
      text: "",
      prompt,
      userId: options?.userId ?? encounterId,
      type
    };
    if (options?.model) {
      body.model = options.model;
    }
    if (options?.responseType != null && options.responseType !== "" && options.responseType.toLowerCase() !== "auto") {
      body.responseType = options.responseType.toLowerCase();
    }
    return this._dataService.postPlainText(url, body);
  }

  generateAIResponse(prompt: string): Observable<any> {
    const url = this.apiUrlForAI + "api/v1/ai/process-text";
    return this._dataService.post(url, { text: "", prompt }).pipe(
      catchError((error) => {
return of(null);
      })
    );
  }

  fetchHpiSummaryWithAI(query: string): Observable<string> {
    const url = this.apiUrlForAI + "api/v1/ai/hpi";
    return this._dataService.postPlainText(url, { query });
  }

  get apiBaseUrl(): string {
    return environment.apiBaseUrl;
  }

  get apiUrlForAI(): string {
    return environment.aiApiUrl;
  }

  uploadAudio(file: File): Observable<any> {
    const url = `${this.apiUrlForAI}api/v1/ai/upload-audio`;
    return this._dataService.uploadFile(url, file).pipe(
      catchError((error) => {
return of(null);
      })
    );
  }

  transcribeAudio(fileKey: string, uniqueSocketSessionId: string, prompt: string): Observable<any> {
    const url = `${this.apiUrlForAI}api/v1/ai/ocr/fileKey/audio`;
    const payload = {
      fileKey: fileKey,
      prompt: prompt,
      userId: uniqueSocketSessionId
    };

    return this._dataService.post(url, payload).pipe(
      catchError((error) => {
return of(null);
      })
    );
  }

  /**
   * Generate document summary using OCR API with multipart/form-data
   * @param file The file to process
   * @param prompt The prompt for summary generation
   * @returns Observable with the generated summary text
   */
  /**
   * Photo ID: multipart OCR with fixed prompt; parses JSON from `parts[0].text`.
   */
  readPhotoIdOcr(file: File): Observable<PhotoIdOcrResult> {
    return this.readOcrMultipartJson<PhotoIdOcrResult>(file, PHOTO_ID_OCR_PROMPT);
  }

  /**
   * Insurance / secondary insurance: card OCR; prompt requires MemberId plus payer/plan fields.
   */
  readInsuranceDocOcr(file: File): Observable<InsuranceOcrResult> {
    return this.readOcrMultipartJson<InsuranceOcrResult>(file, INSURANCE_OCR_PROMPT);
  }

  /** Referral / last-encounter document: provider name + DOS JSON extraction. */
  readReferralDocOcr(file: File): Observable<ReferralDocOcrResult> {
    return this.readOcrMultipartJson<ReferralDocOcrResult>(file, REFERRAL_DOC_OCR_PROMPT);
  }

  /** Last Counter: medications + allergies JSON extraction from referral PDF. */
  readLastCounterMedsAllergiesOcr(
    file: File
  ): Observable<LastCounterMedsAllergiesOcrResult> {
    return this.readOcrMultipartJson<LastCounterMedsAllergiesOcrResult>(
      file,
      LAST_COUNTER_MEDS_ALLERGIES_OCR_PROMPT
    );
  }

  /** Last Counter upload: one OCR pass for summary, provider/DOS, meds, and allergies. */
  readLastCounterCombinedOcr(
    file: File
  ): Observable<LastCounterCombinedOcrResult> {
    return this.readOcrMultipartJson<LastCounterCombinedOcrResult>(
      file,
      LAST_COUNTER_COMBINED_OCR_PROMPT
    );
  }

  /** Shared POST api/v1/ai/ocr multipart + JSON parse from parts[0].text */
  private readOcrMultipartJson<T>(file: File, prompt: string): Observable<T> {
    const url = `${this.apiUrlForAI}api/v1/ai/ocr`;
    const formData = new FormData();
    formData.append("file", file);
    formData.append("prompt", prompt);

    return this._dataService.postFormData(url, formData).pipe(
      map((response: unknown) => {
        const r = response as { parts?: Array<{ text?: string }> };
        const text = r?.parts?.[0]?.text;
        if (text == null || String(text).trim() === "") {
          throw new Error("OCR response missing text");
        }
        const parsed = AiService.parseJsonFromOcrText(String(text));
        if (!parsed) {
          throw new Error("OCR response could not be parsed as JSON");
        }
        if (prompt === LAST_COUNTER_COMBINED_OCR_PROMPT) {
          return normalizeLastCounterCombinedOcrResult(parsed) as T;
        }
        return parsed as T;
      }),
      catchError((error) => {
        console.error("readOcrMultipartJson:", error);
        return throwError(() => error);
      })
    );
  }

  /**
   * Strips markdown code fences (e.g. ```json ... ```) from model OCR text.
   * Repeats in case of double-wrapped output.
   */
  private static stripOcrMarkdownJsonFences(raw: string): string {
    let s = String(raw ?? "").trim();
    for (let i = 0; i < 3; i++) {
      const before = s;
      s = s
        // ```json, ``` JSON, or bare ``` then whitespace/newline before "{"
        .replace(/^```(?:\w+)?\s*/i, "")
        .replace(/\s*```\s*$/g, "")
        .trim();
      if (s === before) {
        break;
      }
    }
    return s.replace(/^\uFEFF/, "");
  }

  /**
   * Takes `parts[0].text` from the OCR API, removes ```json / ``` wrappers,
   * then JSON.parse. Falls back to the substring from first "{" to last "}".
   */
  private static parseJsonFromOcrText(raw: string): Record<string, unknown> | null {
    const cleaned = AiService.stripOcrMarkdownJsonFences(raw);

    const tryParse = (json: string): Record<string, unknown> | null => {
      try {
        return JSON.parse(json) as Record<string, unknown>;
      } catch {
        return null;
      }
    };

    const direct = tryParse(cleaned);
    if (direct) {
      return direct;
    }

    const first = cleaned.indexOf("{");
    const last = cleaned.lastIndexOf("}");
    if (first >= 0 && last > first) {
      return tryParse(cleaned.slice(first, last + 1));
    }
    return null;
  }

  generateDocumentSummary(file: File, prompt: string): Observable<string> {
    const url = `${this.apiUrlForAI}api/v1/ai/ocr`;
    
    // Create FormData for multipart/form-data request
    const formData = new FormData();
    formData.append('file', file);
    formData.append('prompt', prompt);

    return this._dataService.postFormData(url, formData).pipe(
      map((response: any) => {
        // Extract text from JSON response structure: response.parts[0].text
        if (response && response.parts && Array.isArray(response.parts) && response.parts.length > 0) {
          return response.parts[0].text || '';
        }
        // Fallback: return empty string if structure is unexpected
        console.warn('Unexpected response structure from OCR API:', response);
        return '';
      }),
      catchError((error) => {
        console.error('Error generating document summary:', error);
        return throwError(() => error);
      })
    );
  }

  /** Default model for multi-file OpenAI OCR endpoint (api/v1/ai/ocr/multiple/openai) */
  static readonly OCR_MULTIPLE_OPENAI_DEFAULT_MODEL = 'gpt-5.2';

  /**
   * Single multipart request: multiple files + optional prompt + optional model.
   * POST api/v1/ai/ocr/multiple/openai
   * Response: { content: string; model: string }
   * Do not set Content-Type; browser sets it with boundary for FormData.
   */
  generateMultipleDocumentOcrOpenAI(
    files: File[],
    prompt?: string,
    model?: string
  ): Observable<{ content: string; model: string }> {
    const url = `${this.apiUrlForAI}api/v1/ai/ocr/multiple/openai`;
    const formData = new FormData();
    files.forEach((f) => formData.append('files', f, f.name));
    if (prompt) formData.append('prompt', prompt);
    if (model) formData.append('model', model);

    return this._dataService.postFormData(url, formData).pipe(
      map((response: { content?: string; model?: string }) => ({
        content: response?.content ?? '',
        model: response?.model ?? '',
      })),
      catchError((error) => {
        console.error('Error in ocr/multiple/openai:', error);
        return throwError(() => error);
      })
    );
  }
}
